import { useEffect, useState, useCallback } from "react";
import { Outlet, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { AppSidebar } from "@/components/AppSidebar";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { Button } from "@/components/ui/button";
import { Loader2, LogOut, User as UserIcon, Settings } from "lucide-react";
import type { User } from "@supabase/supabase-js";
import { toast } from "sonner";
import { OfflineBanner } from "@/components/OfflineBanner";
import { FeatureErrorBoundary } from "@/components/FeatureErrorBoundary";
import { setSentryUser, clearSentryUser } from "@/lib/sentryUser";
import { OnboardingWizard } from "@/components/onboarding/OnboardingWizard";
import { CommandPalette } from "@/components/CommandPalette";
import { useOrganization } from "@/contexts/OrganizationContext";
import { usePushNotifications } from "@/hooks/usePushNotifications";
import { InstallPromptBanner } from "@/components/InstallPromptBanner";
import { NotificationsBell } from "@/components/NotificationsBell";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { TermsAcceptanceModal } from "@/components/TermsAcceptanceModal";
import { PerformanceLabProvider } from "@/contexts/PerformanceLabContext";
import { PerformanceLabModal } from "@/components/lab/PerformanceLabModal";
import { SectionSubNav } from "@/components/nav/SectionSubNav";
import { useTranslation } from "@/hooks/useTranslation";
import { PulseProvider } from "@/contexts/PulseContext";
import { PulseFloatingButton } from "@/components/pulse/PulseFloatingButton";
import { PulsePanel } from "@/components/pulse/PulsePanel";
import { MobileBottomNav } from "@/components/mobile/MobileBottomNav";
import { usePulseContextSync } from "@/hooks/usePulseContext";
import { ROUTES } from '@/lib/routes';

function PulseContextSync() {
  usePulseContextSync();
  return null;
}


import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

export default function AppLayout() {
  const navigate = useNavigate();
  const { organizations, loading: orgLoading } = useOrganization();
  const { translate: t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState<User | null>(null);
  const [termsRequired, setTermsRequired] = useState(false);

  usePushNotifications(user?.id ?? null);

  useEffect(() => {
    checkAuth();
    document.documentElement.dir = "ltr";
    document.documentElement.lang = "en";
  }, []);

  // Redirect to org onboarding if user has no organizations
  // But first check for a pending invite — if found, redirect to accept it
  useEffect(() => {
    if (!orgLoading && !loading && user && organizations.length === 0) {
      supabase
        .from("org_invites")
        .select("token")
        .eq("invited_email", user.email ?? "")
        .eq("status", "pending")
        .gt("expires_at", new Date().toISOString())
        .order("created_at", { ascending: false })
        .limit(1)
        .then(({ data }) => {
          if (data && data.length > 0) {
            navigate(ROUTES.INVITE(data[0].token));
          } else {
            navigate(ROUTES.ONBOARDING_ORG);
          }
        });
    }
  }, [orgLoading, loading, user, organizations, navigate]);

  const checkTermsAcceptance = useCallback(async (userId: string) => {
    try {
      // Fetch current terms version and user's accepted version in parallel
      const [settingRes, profileRes] = await Promise.all([
        supabase.from("system_settings").select("value").eq("key", "terms_version").single(),
        supabase.from("profiles").select("terms_version_accepted").eq("id", userId).single(),
      ]);
      const currentVersion = settingRes.data?.value ?? "1.0";
      const acceptedVersion = profileRes.data?.terms_version_accepted;
      if (!acceptedVersion || acceptedVersion !== currentVersion) {
        setTermsRequired(true);
      }
    } catch (err) {
      console.error("Terms check failed:", err);
    }
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate(ROUTES.AUTH);
        return;
      }
      
      const { data: orgRoles } = await supabase
        .from("user_organization_roles")
        .select("role")
        .eq("user_id", session.user.id);
      
      const isOnlyPlayer = orgRoles && 
        orgRoles.length > 0 && 
        orgRoles.every(r => r.role === "player_viewer");
      
      if (isOnlyPlayer) {
        navigate(ROUTES.PLAYER_CHECK_IN);
        return;
      }

      const isOnlyParent = orgRoles &&
        orgRoles.length > 0 &&
        orgRoles.every(r => r.role === "parent");

      if (isOnlyParent) {
        navigate(ROUTES.PARENT_PORTAL);
        return;
      }
      
      setUser(session.user);
      setSentryUser(session.user.id, session.user.email);
      // Check terms acceptance after setting user
      await checkTermsAcceptance(session.user.id);
    } catch (error) {
      console.error("Auth check failed:", error);
      navigate(ROUTES.AUTH);
    } finally {
      setLoading(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await supabase.auth.signOut();
      clearSentryUser();
      navigate(ROUTES.AUTH);
    } catch (error: unknown) {
      toast.error(t("app_layout_sign_out_error"));
    }
  };

  // Wait for BOTH auth check AND org loading to complete before rendering
  if (loading || orgLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <PerformanceLabProvider>
    <PulseProvider>
    <PulseContextSync />
    <>

      {/* Terms acceptance gate — blocks entire app until accepted */}
      {termsRequired && user && (
        <TermsAcceptanceModal
          userId={user.id}
          onAccepted={() => setTermsRequired(false)}
        />
      )}
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <FeatureErrorBoundary featureName="Sidebar">
            <AppSidebar />
          </FeatureErrorBoundary>
          <main className="flex-1 flex flex-col">
            <header className="border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60 sticky top-0 z-40">
              <div className="flex items-center justify-between px-4 py-3">
                <SidebarTrigger />
                
                <div className="flex items-center gap-1.5">
                  <LanguageSwitcher />
                  {user && <NotificationsBell />}

                  {user && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="gap-2">
                          <UserIcon className="h-4 w-4" />
                          <span className="hidden sm:inline">{user.email}</span>
                        </Button>
                      </DropdownMenuTrigger>
                       <DropdownMenuContent align="end" className="w-56">
                        <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS_ACCOUNT)}>
                          <UserIcon className="h-4 w-4 mr-2" />
                          {t("app_layout_my_account")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => navigate(ROUTES.SETTINGS)}>
                          <Settings className="h-4 w-4 mr-2" />
                          {t("app_layout_settings")}
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                          <LogOut className="h-4 w-4 mr-2" />
                          {t("app_layout_sign_out")}
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </header>

            <OfflineBanner />
            <InstallPromptBanner />
            <SectionSubNav />
            <div className="flex-1 pb-16 md:pb-0">
              <Outlet />
            </div>
            <MobileBottomNav />
          </main>
        </div>
        <OnboardingWizard />
        <CommandPalette />
      </SidebarProvider>
      {/* Performance Lab modal — rendered at app level so it overlays everything */}
      <PerformanceLabModal />
      <PulsePanel />
      <PulseFloatingButton />

    </>
    </PulseProvider>
    </PerformanceLabProvider>
  );
}
